import { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X } from "lucide-react";
import { Logo } from "./Logo";
import { useLang } from "../i18n/LangContext";
import { scrollToId } from "./SmoothScroll";

const sections = ["services", "work", "approach", "houses", "journal"];

const LangToggle = () => {
  const { lang, setLang } = useLang();
  return (
    <div className="flex items-center font-mono text-xs tracking-widest border border-line rounded-full p-1" data-testid="lang-toggle">
      {["fr", "en"].map((l) => (
        <button
          key={l}
          data-testid={`lang-${l}-btn`}
          onClick={() => setLang(l)}
          className={`px-3 py-1.5 rounded-full uppercase transition-colors duration-300 ${lang === l ? "bg-[var(--gold)] text-[var(--ink)]" : "text-stone hover:text-[var(--bone)]"}`}
        >
          {l}
        </button>
      ))}
    </div>
  );
};

export const Header = () => {
  const { t } = useLang();
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const { pathname } = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const go = (id) => {
    setOpen(false);
    if (pathname !== "/" && !document.getElementById(id)) return navigate(`/#${id}`);
    scrollToId(id);
  };

  return (
    <header
      data-testid="site-header"
      className={`fixed top-0 inset-x-0 z-50 transition-[background-color,padding,border-color] duration-500 border-b ${scrolled ? "bg-[rgba(11,10,9,0.72)] backdrop-blur-xl border-line py-3" : "border-transparent py-6"}`}
    >
      <div className="px-6 md:px-12 flex items-center justify-between gap-6">
        <Link to="/" data-testid="header-logo-link" aria-label="ALLURE HOUSE"><Logo /></Link>
        <nav className="hidden lg:flex items-center gap-9 text-sm text-stone">
          {sections.map((s) => (
            <a key={s} href={`/#${s}`} data-testid={`nav-${s}-link`} onClick={(e) => { e.preventDefault(); go(s); }} className="link-underline hover:text-[var(--bone)] transition-colors duration-300">
              {t.nav[s]}
            </a>
          ))}
        </nav>
        <div className="flex items-center gap-3">
          <LangToggle />
          <button
            data-testid="header-cta-btn"
            onClick={() => go("contact")}
            className="hidden md:inline-flex rounded-full bg-[var(--bone)] text-[var(--ink)] px-5 py-2.5 text-sm font-semibold hover:bg-[var(--gold)] transition-colors duration-300"
          >
            {t.nav.cta}
          </button>
          <button data-testid="mobile-menu-btn" className="lg:hidden p-2" onClick={() => setOpen((o) => !o)} aria-label="menu">
            {open ? <X size={22} /> : <Menu size={22} />}
          </button>
        </div>
      </div>
      <AnimatePresence>
        {open && (
          <motion.nav
            data-testid="mobile-menu"
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            className="lg:hidden px-6 pt-8 pb-10 bg-[var(--ink)] border-b border-line flex flex-col gap-5"
          >
            {[...sections, "contact"].map((s) => (
              <a key={s} href={`/#${s}`} data-testid={`mobile-nav-${s}-link`} onClick={(e) => { e.preventDefault(); go(s); }} className="text-left font-display text-3xl">
                {t.nav[s]}
              </a>
            ))}
          </motion.nav>
        )}
      </AnimatePresence>
    </header>
  );
};
